import { apiFetch, apiUrl, isSuccess } from "./apiClient";
import httpOption from "./httpOption";
import queryClient from "../queryClient";
import { requestRetry } from "../utils/requestRetry";

const FIVE_MINUTES = 1000 * 60 * 5; // 5 min

const createNitroResponseError = (resource, response) => {
  const error = new Error(`Failed to get ${resource}: ${response.status}`);
  error.status = response.status;
  return error;
};

// Test doubles stub only `ok`, so these checks deliberately read
// `response.ok` instead of isSuccess.
async function fetchOkJson(url, request, errorMessage) {
  const response = await apiFetch(url, request);
  if (response.ok) {
    return response.json();
  }
  throw new Error(`${errorMessage}: ${response.status}`);
}

// The XP queries are cached for 5 minutes, so any flow that awards points must
// mark the cached entries stale or the UI keeps showing the old balance.
export async function invalidateNitroUserData() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["nitroUserLevels"] }),
    queryClient.invalidateQueries({ queryKey: ["nitroUserPointBalance"] }),
  ]);
}

export async function getNitroUserData(forceRefresh = false) {
  if (forceRefresh) {
    await invalidateNitroUserData();
  }

  const [userLevels, UserPointBalance] = await Promise.all([
    getNitroUserLevels(),
    getNitroUserPointBalance(),
  ]);
  const EXPBalance = UserPointBalance.find(
    (i) => i.pointCategory?.name === "XP",
  );

  const response = {
    nitroCurrentLevel: userLevels?.current.name,
    nitroCurrentLevelPoints:
      EXPBalance?.currentBalance - userLevels?.current.points,
    nitroUserPoints: EXPBalance?.currentBalance,
    nitroPointsNeeded: userLevels?.next.pointsNeeded,
    nitroNextLevel: userLevels?.next.name,
    nitroNextLevelPoints: userLevels?.next.points,
  };
  return response;
}

export async function getNitroUserLevels() {
  return queryClient.ensureQueryData({
    queryKey: ["nitroUserLevels"],
    staleTime: FIVE_MINUTES,
    queryFn: async () => {
      const response = await apiFetch(apiUrl("/nitro/getNitroUserLevels"));
      if (response.ok ?? isSuccess(response)) {
        return response.json();
      }
      throw createNitroResponseError("nitro user levels", response);
    },
  });
}

export async function getNitroUserPointBalance() {
  return queryClient.ensureQueryData({
    queryKey: ["nitroUserPointBalance"],
    staleTime: FIVE_MINUTES,
    queryFn: async () => {
      const response = await apiFetch(
        apiUrl("/nitro/getNitroUserPointBalance"),
      );
      if (response.ok ?? isSuccess(response)) {
        return response.json();
      }
      throw createNitroResponseError("nitro user point balance", response);
    },
  });
}

async function postUserAction(name, metadata) {
  if (sessionStorage?.getItem("showBackToMission") === "false") {
    return;
  }
  return sendUserAction(
    name,
    metadata,
    `${window.location.pathname}${window.location.search}`,
  );
}

async function sendUserAction(name, metadata, pageUrl) {
  try {
    const body = {
      data: {
        actionTime: Date.now(),
        metadata: {
          ...metadata,
          product: window.ReactNativeWebView ? "ENGAGE APP" : "ENGAGE WEB",
          url: pageUrl,
        },
        name: name,
      },
    };
    const response = await apiFetch(apiUrl("/nitro/postUserAction"), {
      option: "POST",
      body,
    });
    if (isSuccess(response)) {
      return;
    }
    // Log the error response for debugging
    const errorText = await response.text();
    console.error("postUserAction error:", response.status, errorText);
    throw new Error(
      `postUserAction failed with status ${response.status}: ${errorText}`,
    );
  } catch (err) {
    console.error("Exception in postUserAction:", err);
    throw err;
  }
}

const MISSION_VIEW_ACTIONS = new Set(["VIEW_PAGE", "VIEW_ARTICLE"]);

// Credits a mission's view task from the task button itself, so it doesn't depend
// on the destination page reporting the view (redirects, Back to Mission gating).
// Nitro matches the rule on action name + url path (case-insensitive, query included).
export async function postMissionTaskAction(actionName, actionUrl) {
  if (!MISSION_VIEW_ACTIONS.has(actionName)) {
    return;
  }
  const { pathname, search } = new URL(actionUrl, window.location.origin);
  const pageUrl = `${pathname}${search}`;
  return sendUserAction(actionName, { url: pageUrl }, pageUrl);
}

export function postLoginAction() {
  const metadata = {};
  postUserAction("ENGAGE_LOGIN", metadata);
}

export function postViewPageAction(url) {
  const metadata = { url: url };
  postUserAction("VIEW_PAGE", metadata);
}

export function postViewArticleAction(url) {
  const metadata = { url: url };
  postUserAction("VIEW_ARTICLE", metadata);
}

export function postSubmitArticleCommentAction(url) {
  const metadata = { url: url };
  postUserAction("SUBMIT_COMMENT_ARTICLE", metadata);
}

export function postWatchVideoAction(videoId, percentWatched) {
  const metadata = { videoId: videoId, percent_watched: percentWatched };
  return postUserAction("WATCH_VIDEO", metadata);
}

export function postEngageSearchAction(searchString) {
  const metadata = { searchString };
  postUserAction("ENGAGE_SEARCH", metadata);
}

export function postFeaturesSpecsAction(category, label, year, trim, engine) {
  const metadata = {
    name: "FEATURES_SPECS",
    category,
    label,
    year,
    trim,
    engine,
  };
  postUserAction("USE_FEATURE", metadata);
}

export function postTrimWalkAction(category, label, model, year, trim) {
  const curYear = year ? year.toString() : year;
  const metadata = { name: "TRIM_WALK", category, label, model, curYear, trim };
  postUserAction("USE_FEATURE", metadata);
}

export function postVinLookupAction() {
  const metadata = { name: "VIN_LOOKUP" };
  postUserAction("USE_FEATURE", metadata);
}

export function postTcopAction(category, label) {
  const metadata = { name: "TCOP", category, label };
  postUserAction("USE_FEATURE", metadata);
}

export function postColorAction(category, label) {
  const metadata = { name: "COLORS", category, label };
  postUserAction("USE_FEATURE", metadata);
}

export function postTrimSelectorAction(name, category, label) {
  const metadata = { name: name, category, label };
  postUserAction("USE_FEATURE", metadata);
}

export function postTowCapacityAction(modelName, modelYear) {
  const metadata = {
    name: "TOW_CAPACITY",
    model_name: modelName,
    model_year: modelYear,
  };
  postUserAction("USE_FEATURE", metadata);
}

export function postCompetitiveAdvantagesAction(
  category,
  label,
  modelName,
  year,
  jato,
) {
  const featureName = jato
    ? "COMPETITIVE_ADVANTAGES_JATO"
    : "COMPETITIVE_ADVANTAGES_H2H";
  const curYear = year ? year.toString() : year;
  const metadata = { name: featureName, category, label, modelName, curYear };
  postUserAction("USE_FEATURE", metadata);
}

async function fetchListOrEmpty(path, resource, logMessage) {
  try {
    const response = await apiFetch(apiUrl(path));
    if (isSuccess(response)) {
      return await response.json();
    }
    if (response.status === 401 || response.status === 403) {
      // Return empty array when user is not authenticated
      return [];
    }
    throw new Error(`Failed to get ${resource}: ${response.status}`);
  } catch (error) {
    console.error(logMessage, error);
    return [];
  }
}

export async function getNitroRecentActivities(count) {
  return fetchListOrEmpty(
    `/nitro/recent-activities?returnCount=${count}`,
    "recent activities",
    "Failed to fetch nitro recent activities:",
  );
}

export async function getNitroBadges(count) {
  return fetchListOrEmpty(
    "/nitro/badges",
    "nitro badges",
    "Failed to fetch nitro badges:",
  );
}

export async function getUpdatedMission(missionId) {
  const data = await fetchOkJson(
    apiUrl(`/nitro/get-mission/${missionId}`),
    undefined,
    `Failed to get updated mission data for ${missionId}`,
  );
  return data.response;
}

export async function getXPMissions(type) {
  const typeParam = type ? `?type=${type}` : "";
  const data = await fetchOkJson(
    apiUrl(`/nitro/missions${typeParam}`),
    undefined,
    "Failed to get user's missions",
  );
  return data.response;
}

export async function getXPMissionDetails(missionId) {
  const data = await fetchOkJson(
    apiUrl(`/nitro/mission-details/${missionId}`),
    undefined,
    `Failed to get mission details for ${missionId}`,
  );
  return data.response;
}

export async function getXPLeaderboardConfig() {
  return fetchOkJson(
    apiUrl("/nitro/leaderboard-config"),
    undefined,
    "Failed to get leaderboard config",
  );
}

export async function getXPLeaderboardData(leaderboardId, groupClassIds) {
  const data = await fetchOkJson(
    apiUrl(
      `/nitro/leaderboard/?leaderboardId=${leaderboardId}&groupClassId=${groupClassIds}`,
    ),
    undefined,
    `Failed to get leaderboard ${leaderboardId} data`,
  );
  return data.response;
}

export async function getXPLeadersDealerships(leaderbaordData) {
  return fetchOkJson(
    apiUrl("/nitro/leaderboard-dealerships/"),
    { option: "POST", body: leaderbaordData },
    "Failed to get leaderboard users' dealership data",
  );
}

export async function getQuizOverview(quizId) {
  try {
    return await fetchOkJson(
      apiUrl(`/nitro/quiz/${quizId}`),
      undefined,
      `Failed to get quiz overview for quiz ${quizId}`,
    );
  } catch (error) {
    console.log(`Failed to get quiz overview for quiz ${quizId}: ${error}`);
    throw error;
  }
}

export async function getQuizQuestions(quizId) {
  try {
    return await fetchOkJson(
      apiUrl(`/nitro/quiz/${quizId}/questions`),
      undefined,
      `Failed to get questions for quiz ${quizId}`,
    );
  } catch (error) {
    console.log(`Failed to get questions for quiz ${quizId}: ${error}`);
    throw error;
  }
}

export async function getQuizDetails(quizId) {
  try {
    return await fetchOkJson(
      apiUrl(`/nitro/quizDetails/${quizId}`),
      undefined,
      `Failed to get questions for quiz details ${quizId}`,
    );
  } catch (error) {
    console.log(`Failed to get questions for quiz details ${quizId}: ${error}`);
    throw error;
  }
}

export async function sendQuestionAnswers(quizId, questionId, selectedAnswers) {
  const endpoint = apiUrl(
    `/nitro/quiz/${quizId}/questions/${questionId}/responses`,
  );
  const options = httpOption.POST();
  options.body = JSON.stringify({ selectedAnswers });
  try {
    return await requestRetry(endpoint, options);
  } catch (error) {
    console.log(
      `Failed to post answers for quiz ${quizId} and question ${questionId}: ${error}`,
    );
    throw error;
  }
}

export async function postQuizSubmit(quizId) {
  const endpoint = apiUrl(`/nitro/quiz/${quizId}/submit`);
  const options = httpOption.POST();
  try {
    return await requestRetry(endpoint, options);
  } catch (error) {
    console.log(`Failed to submit quiz ${quizId}: ${error}`);
    throw error;
  }
}

export async function selfReportAction(missionId, ruleId) {
  return fetchOkJson(
    apiUrl(`/nitro/self-report/${missionId}/${ruleId}`),
    { option: "POST" },
    `Failed to self report action for ${ruleId}`,
  );
}

export async function getTournamentScores(tournamentName, completedMissions) {
  try {
    const response = await apiFetch(apiUrl("/nitro/getTournamentScores"), {
      option: "POST",
      body: { tournamentName, completedMissions },
    });
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to get user's score data`);
  } catch (error) {
    console.log(error);
  }
}

export async function getTournamentLeaderboard(leaderboardId) {
  const data = await fetchOkJson(
    apiUrl(`/nitro/tournament-leaderboard/?leaderboardId=${leaderboardId}`),
    undefined,
    `Failed to get tournament leaderboard ${leaderboardId} data`,
  );
  return data.response;
}

export async function getTournamentLeaderboardPage(
  leaderboardId,
  groupClassId,
  pageNumber,
  pageSize,
) {
  let path = `/nitro/tournament-leaderboard/?leaderboardId=${leaderboardId}&page=${pageNumber}&pageSize=${pageSize}`;
  if (groupClassId) {
    path += `&groupClassId=${groupClassId}`;
  }
  return fetchOkJson(
    apiUrl(path),
    undefined,
    `Failed to get tournament leaderboard ${leaderboardId} ${pageNumber} data`,
  );
}

export async function postTournamentTransaction(
  pointId,
  pointName,
  amount,
  details,
) {
  try {
    const response = await apiFetch(
      apiUrl("/nitro/postTournamentTransaction"),
      {
        option: "POST",
        body: { pointId, pointName, amount, details },
      },
    );
    if (response.ok) {
      return await response.json();
    }
    throw new Error("Failed to post tournament transaction.");
  } catch (error) {
    console.error("postTournamentTransaction error:", error);
    throw error;
  }
}
